import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import abroadifyLogo from '../assets/img/logo-large.png';

const getPageWidth = (doc) => doc.internal.pageSize.getWidth();
const getPageHeight = (doc) => doc.internal.pageSize.getHeight();

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const addPdfHeader = async (doc) => {
    try {
        const img = await loadImage(abroadifyLogo);
        const maxWidth = 58;
        const maxHeight = 20;
        const imgRatio = img.width / img.height;
        let renderWidth = maxWidth;
        let renderHeight = renderWidth / imgRatio;
        if (renderHeight > maxHeight) {
            renderHeight = maxHeight;
            renderWidth = renderHeight * imgRatio;
        }
        const x = (getPageWidth(doc) - renderWidth) / 2;
        doc.addImage(abroadifyLogo, 'PNG', x, 10, renderWidth, renderHeight);
    } catch {
        doc.setFontSize(18);
        doc.text('ABROADIFY', getPageWidth(doc) / 2, 20, { align: 'center' });
    }
    doc.setDrawColor(192, 217, 238);
    doc.line(14, 32, getPageWidth(doc) - 14, 32);
};

const addPdfFooter = (doc) => {
    const issuingDate = new Date().toLocaleDateString();
    doc.setDrawColor(192, 217, 238);
    const pageHeight = getPageHeight(doc);
    const footerY = pageHeight - 10;
    const lineY = pageHeight - 17;
    const rightX = getPageWidth(doc) - 14;
    doc.line(14, lineY, rightX, lineY);
    doc.setFontSize(9);
    doc.text(`Issuing Date: ${issuingDate}`, 14, footerY);
    doc.textWithLink('app.abroadify.me', rightX, footerY, {
        align: 'right',
        url: 'https://app.abroadify.me',
    });
    doc.setTextColor(0, 0, 0);
};

const addHeaderAndFooterToAllPages = async (doc) => {
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        await addPdfHeader(doc);
        addPdfFooter(doc);
    }
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return String(value);
};

const addSectionTable = (doc, title, rows, startY) => {
    const filteredRows = rows.filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value)) return value.length > 0;
        return String(value).trim() !== '';
    });

    if (!filteredRows.length) return startY;

    if (startY > 240) {
        doc.addPage();
        startY = 42;
    }

    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(title, 14, startY);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
        startY: startY + 4,
        head: [['Field', 'Value']],
        body: filteredRows.map(([label, value]) => [label, formatValue(value)]),
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 2.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            textColor: [45, 45, 45],
        },
        headStyles: {
            fontSize: 10,
            fillColor: [240, 248, 255],
            textColor: [26, 26, 26],
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            fontStyle: 'bold',
        },
        bodyStyles: {
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 127 },
        },
        margin: { top: 42, left: 14, right: 14, bottom: 20 },
    });

    return doc.lastAutoTable.finalY + 10;
};

const addJobUpdatesTable = (doc, rows, startY) => {
    if (startY > 230) {
        doc.addPage();
        startY = 42;
    }

    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text('Job Updates', 14, startY);
    doc.setTextColor(0, 0, 0);

    const body = rows.length
        ? rows.map((row) => [
            formatValue(row.updatedAt),
            formatValue(row.country),
            formatValue(row.companyName),
            formatValue(row.updatedByName),
            formatValue(row.fieldLabel),
            formatValue(row.oldValue),
            formatValue(row.newValue),
            formatValue(row.changeSummary),
        ])
        : [['—', '—', '—', '—', '—', '—', '—', 'No job updates match the selected filters']];

    autoTable(doc, {
        startY: startY + 4,
        head: [['Updated at', 'Country', 'Company', 'Updated by', 'Data point', 'Old value', 'New value', 'Change summary']],
        body,
        theme: 'grid',
        styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            textColor: [45, 45, 45],
        },
        headStyles: {
            fontSize: 8,
            fillColor: [240, 248, 255],
            textColor: [26, 26, 26],
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            fontStyle: 'bold',
        },
        bodyStyles: {
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        columnStyles: {
            0: { cellWidth: 22 },
            1: { cellWidth: 14 },
            2: { cellWidth: 22 },
            3: { cellWidth: 18 },
            4: { cellWidth: 22 },
            5: { cellWidth: 16 },
            6: { cellWidth: 16 },
            7: { cellWidth: 52 },
        },
        margin: { top: 42, left: 14, right: 14, bottom: 20 },
    });

    return doc.lastAutoTable.finalY + 10;
};

export const exportJobsReportPdf = async (rows = [], filters = {}) => {
    const doc = new jsPDF();
    let y = 42;

    doc.setFontSize(18);
    doc.text('Job Updates Report', 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    const filterRows = [
        ['Country', filters.country],
        ['Company', filters.company],
        ['Updated by', filters.updatedBy],
        ['Date from', filters.dateFrom],
        ['Date to', filters.dateTo],
        ['Data point', filters.fieldLabel],
    ];
    const appliedFilters = filterRows.filter(([, value]) => value);

    y = addSectionTable(
        doc,
        'Applied Filters',
        appliedFilters.length ? appliedFilters : [['Filters', 'None']],
        y
    );

    y = addJobUpdatesTable(doc, rows, y);

    await addHeaderAndFooterToAllPages(doc);

    doc.save(`job-updates-report-${Date.now()}.pdf`);
};
